import {
  ClipboardCheck,
  Fuel,
  PlaneLanding,
  AlertTriangle,
  ShieldCheck,
  CreditCard,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { OWNER_LEGAL } from '../../lib/identity';

import { TRACKED_REG } from '../../lib/flightTrack';
// A friendly, scannable summary of the big things a lessee is responsible for
// with N8366X. Plain-language companion to the signed lease (NOT a replacement).
const SECTIONS = [
  {
    icon: ClipboardCheck,
    title: 'Before every flight',
    items: [
      'Do a full preflight — you are pilot in command and hold operational control.',
      'The airplane lives in spot C3 at Million Air North.',
      'Before engine start, look at the beverage-distributor hangar at the end of the ramp — if their door is open or people are working there, hand-pull the airplane clear of the spot first so prop wash doesn’t blow into them.',
      'Check the oil; add oil if it’s below the minimum (you cover it).',
      'Confirm your medical, flight review, and currency are valid.',
      'Check weather and any open squawks before you go.',
    ],
  },
  {
    icon: Fuel,
    title: 'Fuel & oil are on you',
    items: [
      'You pay for your own fuel. AJM is not in the fuel chain and never rebills it.',
      `Return ${TRACKED_REG} to C3 with the tanks topped to full. Fueling somewhere cheaper en route is fine — the last top-off happens at Long Beach.`,
      'Make sure the FBO charges your card, not the aircraft. Confirm it before you head out.',
      'Never operate or return the airplane below FAA legal reserves (91.151 / 91.167).',
      'Check the oil before every flight. When adding oil, fill to 7 quarts — not 8. The engine holds 8, but the 8th quart just blows out the breather.',
      'Report low oil as a squawk.',
    ],
  },
  {
    icon: PlaneLanding,
    title: 'When you return',
    items: [
      'Bring it back to KLGB — spot C3 at Million Air North — the same day, unless you told the owner you’re keeping it overnight.',
      'Overnights and trips over 8 hours are yours to make. Just give the owner the itinerary in advance so the airplane and the insurance are accounted for — it’s a heads-up, not a permission slip.',
      'Secure it with the tie-down straps and chocks.',
      'Leave the cabin clean and take all your personal items.',
      'Don’t duplicate or lend the key. Keep your KLGB badge current — without one you need a badged escort every time you go to the airplane.',
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Report it — in the portal',
    items: [
      'Log your flight (Hobbs/tach) before you leave the aircraft.',
      'Report squawks immediately — before further flight if you find it on preflight, right after shutdown if you find it in the air. Not tomorrow.',
      'You can ground the airplane yourself. If something looks unairworthy, file the squawk as “Grounding” and nobody flies until it’s resolved. You will never be penalized or charged for grounding it in good faith.',
      'Never fly a known grounding squawk.',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Stay legal & insured',
    items: [
      'Keep your medical, flight review, ratings, and currency current — tell us if anything changes.',
      `Carry renter’s / non-owned aircraft insurance. It is required, not optional. Name AJM Aviation Ventures, LLC and ${OWNER_LEGAL} (individually) as additional insured, and send a certificate before your first solo flight and at every renewal.`,
      `Don’t assume AJM’s policy covers you. It insures the airplane and the owner — if you damage ${TRACKED_REG}, that insurer pays the owner and then comes after you to recover it. Whatever it extends on your behalf is limited.`,
      'Your own policy is what stands between you and that. It also pays for your legal defense if a passenger or someone on the ground makes a claim — often the bigger bill of the two.',
      'If you damage the airplane, you are responsible for the repair cost — not just a deductible. Carry enough non-owned hull coverage to match. Ask the owner for the current figure before you buy a policy.',
      'Continental US only — no Mexico, Canada, or out-of-country flights. AJM’s policy would cover you up there; the lease is what says don’t.',
      'No compensated/commercial flying and no prohibited uses under the policy.',
    ],
  },
  {
    icon: CreditCard,
    title: 'Billing',
    items: [
      'No monthly minimum — you pay only for the Hobbs hours you actually fly.',
      `Every Hobbs hour bills at $100/hr per flight; each night ${TRACKED_REG} stays away from Long Beach adds 2 hrs. Same-day trips add nothing.`,
      'Keep a valid credit card on file at all times.',
    ],
  },
];

export function Responsibilities() {
  return (
    <>
      <PageHeader
        title="Your responsibilities"
        subtitle={`The big things to remember when you have ${TRACKED_REG}.`}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SECTIONS.map((s) => (
          <div key={s.title} className="card p-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-navy-700">
                <s.icon size={18} />
              </span>
              <h2 className="font-display text-base font-semibold text-navy-900">{s.title}</h2>
            </div>
            <ul className="mt-3 space-y-2">
              {s.items.map((it) => (
                <li key={it} className="flex gap-2 text-sm text-ink">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-5 rounded-lg bg-sky-50 px-4 py-3 text-xs text-ink-muted">
        This is a friendly summary to help you remember the essentials. Your signed lease agreement is
        the full and governing document — when in doubt, refer to it or ask the owner.
      </p>
    </>
  );
}
